"use strict";
/**
 * APITable <https://github.com/apitable/apitable>
 * Copyright (C) 2022 APITable Ltd. <https://apitable.com>
 *
 * This program is free software: you can redistribute it and/or modify
 * it under the terms of the GNU Affero General Public License as published by
 * the Free Software Foundation, either version 3 of the License, or
 * (at your option) any later version.
 *
 * This program is distributed in the hope that it will be useful,
 * but WITHOUT ANY WARRANTY; without even the implied warranty of
 * MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
 * GNU Affero General Public License for more details.
 *
 * You should have received a copy of the GNU Affero General Public License
 * along with this program.  If not, see <http://www.gnu.org/licenses/>.
 */
Object.defineProperty(exports, "__esModule", { value: true });
exports.addFields = void 0;
const command_manager_1 = require("../../command_manager");
const store_1 = require("../../exports/store");
const field_types_1 = require("../../types/field_types");
const utils_1 = require("../../utils");
const field_1 = require("../common/field");
const __1 = require("..");
const model_1 = require("../../model");
const i18n_1 = require("../../exports/i18n");
const selectors_1 = require("../../exports/store/selectors");
const types_1 = require("../../types");
const set_records_1 = require("./set_records");
exports.addFields = {
    undoable: true,
    execute: (context, options) => {
        const { model: state } = context;
        const activeDatasheetId = store_1.Selectors.getActiveDatasheetId(state);
        const { data, copyCell, internalFix, fieldId, datasheetId = activeDatasheetId } = options;
        const snapshot = store_1.Selectors.getSnapshot(state, datasheetId);
        const recordMap = snapshot.recordMap;
        const maxFieldCountPerSheet = (0, model_1.getMaxFieldCountPerSheet)();
        if (!snapshot || data.length === 0) {
            return null;
        }
        const isOverLimit = data.length + snapshot.meta.views[0].columns.length > maxFieldCountPerSheet;
        if (isOverLimit) {
            throw new Error((0, i18n_1.t)(i18n_1.Strings.columns_count_limit_tips, {
                column_limit: maxFieldCountPerSheet,
            }));
        }
        const newFieldIds = (0, utils_1.getNewIds)(utils_1.IDPrefix.Field, data.length, Object.keys(snapshot.meta.fieldMap));
        const frozenCountMap = new Map();
        let newFieldId = '';
        const linkedActions = [];
        const actions = data.reduce((collected, fieldOption, index) => {
            var _a;
            newFieldId = newFieldIds[index];
            const { index: columnIndex, viewId } = fieldOption;
            const view = snapshot.meta.views.find(view => view.id === viewId);
            const frozenColumnCount = view === null || view === void 0 ? void 0 : view.frozenColumnCount;
            // special handling for associated fields
            // When the table associated with the newly added associated field cannot be queried in the state, 
            // an association cannot be established at this time.
            // Here we convert this field directly to a text field.
            if (fieldOption.data.type === field_types_1.FieldType.Link &&
                !store_1.Selectors.getDatasheet(state, fieldOption.data.property.foreignDatasheetId)) {
                fieldOption = Object.assign(Object.assign({}, fieldOption), { data: Object.assign(Object.assign({}, fieldOption.data), { type: field_types_1.FieldType.Text, property: null }) });
            }
            const field = Object.assign(Object.assign({}, fieldOption.data), { id: fieldOption.data.id ? fieldOption.data.id : newFieldId, property: fieldOption.data.property });
            // Calculated fields need to determine their own datasheet through the field property, 
            // here we force him to specify the datasheetId of the current command
            if (model_1.Field.bindContext(field, state).isComputed) {
                field.property = Object.assign(Object.assign({}, field.property), { datasheetId });
            }
            if (field.type === field_types_1.FieldType.Link) {
                field.property = Object.assign(Object.assign({}, field.property), { brotherFieldId: undefined });
                const linkedAction = (0, field_1.createNewBrotherField)(state, field, datasheetId);
                linkedAction && linkedActions.push(linkedAction);
            }
            // AutoNumber needs to record the current view index
            if (field.type === field_types_1.FieldType.AutoNumber) {
                const datasheet = (0, selectors_1.getDatasheet)(state, datasheetId);
                const viewIdx = snapshot.meta.views.findIndex(item => item.id === (datasheet === null || datasheet === void 0 ? void 0 : datasheet.activeView)) || 0;
                field.property = Object.assign(Object.assign({}, field.property), { viewIdx });
            }
            if (field.type === field_types_1.FieldType.CreatedBy || field.type === field_types_1.FieldType.LastModifiedBy) {
                const uuids = model_1.Field.bindContext(field, state).getUuidsByRecordMap(recordMap);
                field.property = Object.assign(Object.assign({}, field.property), { uuids });
            }
            const selfCreateNewField = (_a = internalFix === null || internalFix === void 0 ? void 0 : internalFix.selfCreateNewField) !== null && _a !== void 0 ? _a : true;
            // Special repair one-way association, if False, this table does not create a new field
            if (selfCreateNewField) {
                const action = (0, field_1.createNewField)(snapshot, field, fieldOption);
                collected.push(...action);
            }
            if (copyCell) {
                const recordData = Object.keys(recordMap).reduce((data, recordId) => {
                    if (!fieldId)
                        return data;
                    const value = recordMap[recordId].data[fieldId];
                    if (!value)
                        return data;
                    data.push({
                        recordId,
                        fieldId: newFieldId,
                        field,
                        value,
                    });
                    return data;
                }, []);
                const ret = set_records_1.setRecords.execute(context, {
                    cmd: __1.CollaCommandName.SetRecords,
                    data: recordData,
                    internalFix: internalFix,
                });
                if (ret && ret.result === command_manager_1.ExecuteResult.Success) {
                    collected.push(...ret.actions);
                }
            }
            if ((internalFix === null || internalFix === void 0 ? void 0 : internalFix.changeOneWayLinkDstId) && linkedActions.length) {
                const fixOneWayLinkData = {
                    oldBrotherFieldId: snapshot.meta.fieldMap[fieldId].property.brotherFieldId,
                    newBrotherFieldId: linkedActions[0].actions[0]['li']['fieldId'],
                };
                linkedActions[0].actions[linkedActions[0].actions.length - 1]['oi'].property.brotherFieldId = fieldId;
                // Fix one-way association column DstId
                const result = __1.fixOneWayLinkDstId.execute(context, {
                    cmd: __1.CollaCommandName.FixOneWayLinkDstId,
                    data: [fixOneWayLinkData],
                    datasheetId: datasheetId,
                    fieldId: fieldId,
                });
                if (result && result.result === command_manager_1.ExecuteResult.Success) {
                    collected.push(...result.actions);
                }
            }
            if (frozenColumnCount && columnIndex < frozenColumnCount) {
                const count = (frozenCountMap.get(viewId) || frozenColumnCount) + 1;
                const action = model_1.DatasheetActions.setFrozenColumnCount2Action(snapshot, { viewId: viewId, count: count });
                if (action) {
                    frozenCountMap.set(viewId, frozenColumnCount + 1);
                    collected.push(action);
                }
            }
            return collected;
        }, []);
        if (actions.length === 0) {
            return null;
        }
        return {
            result: command_manager_1.ExecuteResult.Success,
            resourceId: datasheetId,
            resourceType: types_1.ResourceType.Datasheet,
            data: newFieldId,
            actions,
            datasheetId,
            linkedActions,
        };
    },
};
/*

 declare module '../../command_manager/command_manager' {
 interface CollaCommandManager {
 execute(options: IAddFieldsOptions & { cmd: 'AddFields' });
 }
 }

 */
//# sourceMappingURL=add_fields.js.map